import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import './RsvpShareControls.css';

// Render only for survey builders. The server independently checks that permission.
export default function RsvpShareControls({ slug, kind }) {
    const digital = kind === 'digital_attendance';
    const label = digital ? 'attendance' : 'RSVP';
    const [link, setLink] = useState(null);
    const [message, setMessage] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [showQr, setShowQr] = useState(false);
    const [qrReady, setQrReady] = useState(false);
    const canvas = useRef(null);
    const localOnly = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    useEffect(() => {
        let active = true;
        setQrReady(false);
        if (!showQr || !link || !canvas.current) return;
        QRCode.toCanvas(canvas.current, link.url, {width:1200, margin:4, errorCorrectionLevel:'M', color:{dark:'#000000',light:'#ffffff'}})
            .then(() => { if (active) setQrReady(true); })
            .catch(() => { if (active) setMessage('Could not create the QR code. Close it and try again.'); });
        return () => { active = false; };
    }, [showQr, link]);
    const whatsAppMessage = link ? `${link.title}\n${digital ? 'Record your attendance at the meeting:' : 'Please let us know whether you will attend:'}\n${link.url}\n${digital ? 'No login or token to enter. Please also sign the physical register.' : 'No login or token to enter: just your name, lodge and Yes or No.'}` : '';

    useEffect(() => {
        let active = true;
        setShowQr(false); setLink(null); setMessage(`Preparing ${label} link…`);
        axios.post(`/api/surveys/${encodeURIComponent(slug)}/${digital ? 'quick-attendance-link' : 'quick-rsvp-link'}`)
            .then(({ data }) => { if (active) { setLink({ ...data, url: new URL(data.path, window.location.origin).href }); setMessage(''); } })
            .catch(error => { if (active) setMessage(error.response?.data?.error || 'Could not prepare the RSVP link. Please retry.'); });
        return () => { active = false; };
    }, [slug, attempt, digital, label]);
    return <div className="rsvp-share-controls">
        {link && <>
            <div className="rsvp-share-buttons">
                <a href={link.url} target="_blank" rel="noopener noreferrer">Open simple {digital ? 'attendance' : 'RSVP'}</a>
                <button type="button" onClick={async () => {
                    try { await navigator.clipboard.writeText(link.url); setMessage(`${label} link copied. Paste it into any group or email.`); }
                    catch { setMessage('Copy the link from the box below.'); }
                }}>Copy {label} link</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(whatsAppMessage)}`} target="_blank" rel="noopener noreferrer">Share on WhatsApp</a>
                <button type="button" aria-expanded={showQr} onClick={() => setShowQr(value => !value)}>{showQr ? 'Hide QR code' : 'QR code for agenda'}</button>
            </div>
            {showQr && <section className="meeting-share-qr" aria-label={`${label} QR code`}>
                <strong>{link.title}</strong>
                <canvas ref={canvas} role="img" aria-label={`Scan to open simple ${label} for ${link.title}`} />
                {!qrReady && <span role="status">Creating QR code…</span>}
                <div className="rsvp-share-buttons">
                    <button type="button" disabled={!qrReady} onClick={() => {
                        const download = document.createElement('a');
                        download.href = canvas.current.toDataURL('image/png');
                        download.download = `${slug}-${label.toLowerCase()}-qr.png`;
                        document.body.appendChild(download); download.click(); download.remove();
                        setMessage('QR image downloaded. Insert it into your agenda or attach it in WhatsApp.');
                    }}>Download QR image</button>
                    <button type="button" disabled={!qrReady} onClick={async () => {
                        try {
                            const blob = await new Promise(resolve => canvas.current.toBlob(resolve, 'image/png'));
                            await navigator.clipboard.write([new ClipboardItem({'image/png':blob})]);
                            setMessage('QR image copied. Paste it into your agenda or document.');
                        } catch { setMessage('This browser cannot copy images. Use Download QR image, then insert the PNG into your agenda.'); }
                    }}>Copy QR image</button>
                </div>
                <small>Scan to open this meeting’s simple {label} form. Insert the PNG into your agenda at about 4 cm wide or larger, keeping the white border.</small>
            </section>}
            {localOnly && <small className="meeting-share-local">Local testing only: generate the link and QR code on the live website before sharing with phones or printing agendas.</small>}
            <label className="rsvp-share-link">Ready-to-share link<input aria-label={`${label} sharing link`} readOnly value={link.url} onFocus={event => event.target.select()} /></label>
            <small>No login needed. Share the same link in every group; replies go into this survey.</small>
        </>}
        {message && <span role="status">{message}</span>}
        {!link && message !== `Preparing ${label} link…` && <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>}
    </div>;
}
